import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type TechnicianAvailability = components['schemas']['TechnicianAvailabilityResponse'];
export type TechnicianTimeOff = components['schemas']['TechnicianTimeOffResponse'];
export type TimeOffKind = components['schemas']['TimeOffKind'];

/**
 * Availability and time off for one person (M1-39).
 *
 * Keyed on the USER id, like skills (M1-38) and unlike licences (M1-37) — see
 * api/CONVENTIONS.md's M1-39 section for the third telling of that decision and
 * the rule it finally settles.
 */

/**
 * The weekdays, in calendar order, as a value the UI can iterate.
 *
 * ⚠️ <b>Sunday first, matching `boardWeek`'s own week-start rule</b> rather
 * than being re-derived here — the board already decided that question for the
 * account's locale, and a picker that ran Monday-first would disagree with the
 * week view sitting next to it.
 *
 * These are the `DayOfWeek` NAMES the API stores and returns; nothing here ever
 * sends an ordinal, for the reason `DayHoursResponse` states at length.
 */
export const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export type Weekday = (typeof WEEKDAYS)[number];

/**
 * The kinds the AC names. `satisfies` pins each to a real member of the
 * generated union, so a typo or a REMOVED kind is a type error — the same
 * trick and the same caveat as `SKILL_PROFICIENCIES` (M1-38): a kind ADDED on
 * the API side still compiles green and simply never appears in the picker.
 */
export const TIME_OFF_KINDS = [
  'Pto',
  'Sick',
  'Training',
  'Other',
] as const satisfies readonly TimeOffKind[];

/** Thrown for the 403 a technician gets when looking at someone ELSE's availability. */
export class AvailabilityNotPermittedError extends Error {
  constructor() {
    super('Not permitted to view this availability');
    this.name = 'AvailabilityNotPermittedError';
  }
}

function availabilityKey(userId: string) {
  return ['users', userId, 'availability'] as const;
}

function timeOffKey(userId: string) {
  return ['users', userId, 'time-off'] as const;
}

/**
 * Everything a mutation here has to refresh.
 *
 * ⚠️ The board's own composite read (`['dispatch']`) is invalidated too, and
 * that is not optional: it carries a SEPARATE, pre-resolved copy of this
 * answer, so editing somebody's hours without invalidating it leaves the
 * dispatch board greying them exactly as it did before the change — with no
 * error anywhere to suggest the edit had not taken.
 */
function invalidate(queryClient: ReturnType<typeof useQueryClient>, userId: string) {
  void queryClient.invalidateQueries({ queryKey: availabilityKey(userId) });
  void queryClient.invalidateQueries({ queryKey: timeOffKey(userId) });
  void queryClient.invalidateQueries({ queryKey: ['dispatch'] });
}

export function useTechnicianAvailability(userId: string) {
  return useQuery({
    queryKey: availabilityKey(userId),
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/users/{userId}/availability', {
        params: { path: { userId } },
      });
      if (response.status === 403) {
        throw new AvailabilityNotPermittedError();
      }
      if (!response.ok || !data) {
        throw new Error(`GET availability failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

export function useTechnicianTimeOff(userId: string) {
  return useQuery({
    queryKey: timeOffKey(userId),
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/users/{userId}/time-off', {
        params: { path: { userId } },
      });
      if (response.status === 403) {
        throw new AvailabilityNotPermittedError();
      }
      if (!response.ok || !data) {
        throw new Error(`GET time off failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

export interface AvailabilityInput {
  day: Weekday;
  startTime: string;
  endTime: string;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export function useSaveAvailability(userId: string, id: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AvailabilityInput) => {
      const body = {
        day: input.day,
        startTime: input.startTime,
        endTime: input.endTime,
        effectiveFrom: input.effectiveFrom,
        effectiveTo: input.effectiveTo,
      };
      const { data, response } = id
        ? await apiClient.PUT('/api/v1/users/{userId}/availability/{id}', {
            params: { path: { userId, id } },
            body,
          })
        : await apiClient.POST('/api/v1/users/{userId}/availability', {
            params: { path: { userId } },
            body,
          });
      if (!response.ok || !data) {
        throw new Error(`Save availability failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidate(queryClient, userId),
  });
}

export function useDeleteAvailability(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { response } = await apiClient.DELETE('/api/v1/users/{userId}/availability/{id}', {
        params: { path: { userId, id } },
      });
      if (!response.ok) {
        throw new Error(`Delete availability failed: ${response.status}`);
      }
    },
    onSuccess: () => invalidate(queryClient, userId),
  });
}

export interface TimeOffInput {
  kind: TimeOffKind;
  startDate: string;
  endDate: string;
  notes: string | null;
}

export function useSaveTimeOff(userId: string, id: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: TimeOffInput) => {
      const body = {
        kind: input.kind,
        startDate: input.startDate,
        endDate: input.endDate,
        notes: input.notes,
      };
      const { data, response } = id
        ? await apiClient.PUT('/api/v1/users/{userId}/time-off/{id}', {
            params: { path: { userId, id } },
            body,
          })
        : await apiClient.POST('/api/v1/users/{userId}/time-off', {
            params: { path: { userId } },
            body,
          });
      if (!response.ok || !data) {
        throw new Error(`Save time off failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidate(queryClient, userId),
  });
}

export function useDeleteTimeOff(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { response } = await apiClient.DELETE('/api/v1/users/{userId}/time-off/{id}', {
        params: { path: { userId, id } },
      });
      if (!response.ok) {
        throw new Error(`Delete time off failed: ${response.status}`);
      }
    },
    onSuccess: () => invalidate(queryClient, userId),
  });
}
